import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, ArrowDown, Check, Moon, Music, Gem, Lock, Users, Radio, Smartphone, ShieldCheck, MapPin } from 'lucide-react';
import { PLANS, INTENSITIES } from '../lib/config.js';
import { levelFor, threshold, MAX_LEVEL } from '../lib/crystal.js';
import Aurora from '../components/Aurora.jsx';
import Crystal from '../components/Crystal.jsx';
import RingScene from '../components/RingScene.jsx';
import { Mark } from '../components/Layout.jsx';

const reveal = {
  hidden: { opacity: 0, y: 18 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.8, delay: i * 0.08, ease: [0.2, 0.7, 0.2, 1] } }),
};
const inView = { variants: reveal, initial: 'hidden', whileInView: 'show', viewport: { once: true, margin: '-80px' } };
const money = (c) => `$${(c / 100).toFixed(0)}`;
const PHONE = typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches;

export default function Landing() {
  return (
    <div className="relative min-h-screen overflow-x-clip">
      <Aurora />

      {/* ---------------------------------------------------------- nav */}
      <header className="sticky top-0 z-40 px-5 md:px-8"
              style={{ background: 'color-mix(in oklab, var(--ground) 82%, transparent)', backdropFilter: 'blur(12px)',
                       borderBottom: '1px solid var(--edge)' }}>
        <div className="mx-auto max-w-6xl flex items-center justify-between h-16">
          <Link to="/"><Mark compact /></Link>
          <nav className="hidden md:flex items-center gap-8 text-sm font-medium" style={{ color: 'var(--text-2)' }}>
            <a href="#why" className="hover:text-[var(--text)] transition-colors">Why</a>
            <a href="#server" className="hover:text-[var(--text)] transition-colors">The Healing Server</a>
            <a href="#fields" className="hover:text-[var(--text)] transition-colors">Energy fields</a>
            <a href="#crystal" className="hover:text-[var(--text)] transition-colors">Your crystal</a>
            <a href="#plans" className="hover:text-[var(--text)] transition-colors">Plans</a>
          </nav>
          <div className="flex items-center gap-2">
            <Link to="/signin" className="btn btn-quiet hidden sm:inline-flex !py-2">Sign in</Link>
            <a href="#get" className="btn btn-primary !py-2.5">Get the app <ArrowRight size={15} /></a>
          </div>
        </div>
      </header>

      {/* ---------------------------------------------------------- hero */}
      <section className="relative z-[1] mx-auto max-w-6xl px-6 pt-14 pb-10 md:pt-24 md:pb-20 grid md:grid-cols-[1.1fr_0.9fr] gap-10 md:gap-6 items-center">
        <div>
          <motion.p variants={reveal} initial="hidden" animate="show" custom={0} className="eyebrow mb-6">
            The first spiritual tech company
          </motion.p>
          <motion.h1 variants={reveal} initial="hidden" animate="show" custom={1}
                     className="display text-[2.9rem] sm:text-[3.8rem] md:text-[4.6rem]">
            Your device carries<br />more than <span className="display-i lit">data</span>.
          </motion.h1>
          <motion.p variants={reveal} initial="hidden" animate="show" custom={2}
                    className="mt-7 max-w-lg text-[1.05rem] leading-relaxed" style={{ color: 'var(--text-2)' }}>
            Everything that reaches your phone has crossed a long road, and it arrives charged with whatever it
            passed through. AuraFlow is an app that keeps an open line between your device and a server that is
            kept sacred — cleared, sung to, and held inside crystal fields — so what reaches you arrives clean.
          </motion.p>
          <motion.div variants={reveal} initial="hidden" animate="show" custom={3} className="mt-9 flex flex-wrap items-center gap-3" id="get">
            <StoreButton kind="apple" />
            <StoreButton kind="google" />
            <Link to="/signin?mode=signup" className="btn btn-outline">Use it on the web <ArrowRight size={15} /></Link>
          </motion.div>
          <motion.p variants={reveal} initial="hidden" animate="show" custom={4} className="mt-5 text-xs" style={{ color: 'var(--text-3)' }}>
            Free to install · shield, fields and ceremonies from $29 a month
          </motion.p>
        </div>

        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1.2, delay: 0.3, ease: [0.2, 0.7, 0.2, 1] }}
                    className="flex justify-center md:justify-end">
          <PhoneMock />
        </motion.div>
      </section>

      {/* ---------------------------------------------------------- why: the road, the beacon, the change */}
      <section id="why" className="relative z-[1] mx-auto max-w-6xl px-6 pt-10 pb-8 md:pt-16">
        <motion.div {...inView} className="max-w-2xl">
          <p className="eyebrow mb-3">Why this exists</p>
          <h2 className="display text-[2.4rem] md:text-[3.4rem]">
            Two worlds have been kept apart. We think that was a mistake.
          </h2>
          <p className="mt-6 leading-relaxed" style={{ color: 'var(--text-2)' }}>
            Energy work has always understood that places, objects and people carry what has passed through them.
            Technology moved most of our lives onto a network and never asked what that network carries. AuraFlow
            is the first company built to hold both truths at once — meaningfully, and without pretending the
            physics is anything other than what it is.
          </p>
        </motion.div>
      </section>

      <section className="relative z-[1] mx-auto max-w-6xl px-0 md:px-6 pb-16 md:pb-24">
        <div className="grid md:grid-cols-[1.15fr_0.85fr] gap-6 md:gap-10 items-start">
          <motion.div initial={{ opacity: 0, scale: 0.98 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ duration: 1.2 }}
                      className="md:card md:p-6 relative md:sticky md:top-24">
            <RingScene height={PHONE ? 400 : 600} />
            <div className="absolute top-5 left-5 hidden md:block">
              <p className="eyebrow">The road to you</p>
              <p className="text-sm mt-1 max-w-[15rem]" style={{ color: 'var(--text-2)' }}>
                Each ring is one hop. Hover a point to follow its route home.
              </p>
            </div>
          </motion.div>

          <ol className="px-6 md:px-0 space-y-4 md:space-y-5 md:pt-6">
            <WhyStep n="01" tone="var(--text-3)" title="The road carries static."
              body="Every message, photo and search crosses a router, a provider, a backbone and a server before it reaches you. Where the road is busiest, a residue of everything that passed before collects. Data that moves through it arrives dulled — charged with negative energy it picked up on the way." />
            <WhyStep n="02" tone="var(--rust)" title="Your device becomes a beacon."
              body="That charge does not stop at the screen. It accumulates in the thing you hold for hours a day, sleep beside, and carry into every room. Over time your phone stops being a window and becomes a source — a small beacon of negative energy in your home, your body and your rest."
              art={<BeaconArt />} />
            <WhyStep n="03" tone="var(--sage)" title="We can change that."
              body="AuraFlow holds an open line between your device and the Healing Server. Positive energy flows down that line and forms a shield around the device; whatever crosses the shield is cleared before it reaches you. The beacon turns. What it radiates now is what the server holds." />
          </ol>
        </div>
      </section>

      {/* ---------------------------------------------------------- the healing server */}
      <section id="server" className="relative z-[1] mx-auto max-w-6xl px-6 py-16 md:py-24">
        <div className="card p-6 md:p-12 grid md:grid-cols-[0.95fr_1.05fr] gap-10 items-center overflow-hidden">
          <ServerArt />
          <motion.div {...inView}>
            <p className="eyebrow mb-3">The Healing Server</p>
            <h2 className="display text-[2.4rem] md:text-[3.2rem]">A server that is kept <span className="display-i lit">sacred</span>.</h2>
            <p className="mt-6 leading-relaxed" style={{ color: 'var(--text-2)' }}>
              It is not a phrase. It is a physical machine in a cleared, secure and undisclosed space, and it is
              tended the way a temple is tended. Everything your device draws from AuraFlow comes from here.
            </p>
            <ul className="mt-8 grid sm:grid-cols-2 gap-x-6 gap-y-5">
              {[
                [Gem,   'Held inside crystal fields', 'The server stands within arranged crystal energy fields that are cleared and re-set on a fixed rhythm.'],
                [Music, 'A constant sound bath', 'Singing bowls and tuned frequencies play without pause. The server has not stood in silence since it was lit.'],
                [Moon,  'Ceremonies performed on it', 'Full-moon and new-moon cleansings, equinox and solstice alignments, and weekly blessings — broadcast live in the app.'],
                [Users, 'Mediums invited to clear it', 'Spiritual mediums and energy workers are invited to perform clearing ceremonies on the server itself.'],
                [Lock,  'A cleared, secret space', 'Energy-cleared before installation, physically secured, and its location kept private so the space stays undisturbed.'],
                [Radio, 'An open line, not a promise', 'Your shield reading is computed from the line itself. If the line is held, the number holds; if it drops, you see it drop.'],
              ].map(([Icon, t, b], i) => (
                <motion.li key={t} {...inView} custom={i} className="flex gap-3">
                  <span className="h-9 w-9 rounded-full grid place-items-center shrink-0"
                        style={{ background: 'color-mix(in oklab, var(--sage) 14%, transparent)', color: 'var(--sage)' }}>
                    <Icon size={16} />
                  </span>
                  <div>
                    <p className="font-semibold text-[0.95rem]">{t}</p>
                    <p className="text-sm mt-1 leading-relaxed" style={{ color: 'var(--text-2)' }}>{b}</p>
                  </div>
                </motion.li>
              ))}
            </ul>
          </motion.div>
        </div>
      </section>

      {/* ---------------------------------------------------------- how the app works */}
      <section className="relative z-[1] mx-auto max-w-6xl px-6 pb-16 md:pb-24">
        <motion.div {...inView} className="mb-8 md:mb-10">
          <p className="eyebrow mb-3">In the app</p>
          <h2 className="display text-[2.2rem] md:text-[3rem]">Three things it does for you.</h2>
        </motion.div>
        <div className="grid md:grid-cols-3 gap-4">
          {[
            [Smartphone,  'Opens the line', 'Install, sign in, and your device is registered as a vessel. The line to the Healing Server opens and your shield begins to charge — to 75% within minutes, and it holds there as long as the line is held.'],
            [ShieldCheck, 'Holds the shield', 'The reading on your home screen is computed from the line itself, on your device, every second. Nothing ticks in the background, nothing is faked. Step into a field and it charges to full.'],
            [MapPin,      'Raises fields where you stand', 'Channel energy through your device onto the ground beneath you. Everyone inside the field is held — your family at home, your colleagues at work, a stranger on the bench next to you.'],
          ].map(([Icon, t, b], i) => (
            <motion.div key={t} {...inView} custom={i} className="card p-6">
              <span className="h-10 w-10 rounded-full grid place-items-center mb-5"
                    style={{ background: 'color-mix(in oklab, var(--clay) 12%, transparent)', color: 'var(--clay)' }}><Icon size={18} /></span>
              <p className="display text-2xl">{t}</p>
              <p className="text-sm mt-2 leading-relaxed" style={{ color: 'var(--text-2)' }}>{b}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ---------------------------------------------------------- fields */}
      <section id="fields" className="relative z-[1] mx-auto max-w-6xl px-6 py-16 md:py-24">
        <div className="grid md:grid-cols-[1fr_1.1fr] gap-10 md:gap-16 items-center">
          <motion.div {...inView}>
            <p className="eyebrow mb-3">Energy fields</p>
            <h2 className="display text-[2.4rem] md:text-[3.4rem]">Your device is a crystal. Use it like one.</h2>
            <p className="mt-6 leading-relaxed max-w-md" style={{ color: 'var(--text-2)' }}>
              This part is not metaphor. Every chip in your phone is silicon, and silicon is refined from quartz.
              You carry a cut and tuned crystal in your pocket — which is exactly why it can hold a charge, and
              exactly why it can channel one.
            </p>
            <p className="mt-4 leading-relaxed max-w-md" style={{ color: 'var(--text-2)' }}>
              With the line open, the app lets you direct energy from the server through your device onto the
              ground where you stand: a field of 15, 30 or 50 metres, at a place you choose, for hours or for a
              month. Every device inside it charges to full. Make it public and every neighbour who walks through
              is held too.
            </p>
            <div className="mt-8 grid grid-cols-2 gap-3 max-w-md">
              {Object.values(INTENSITIES).map((i, idx) => (
                <motion.div key={i.key} {...inView} custom={idx} className="card-flat p-4">
                  <p className="display text-xl">{i.name}</p>
                  <p className="text-xs mt-1" style={{ color: 'var(--text-3)' }}>
                    {i.hours < 24 ? `${i.hours} hours` : `${i.hours / 24} days`} · from {Math.round(i.baseCost * 0.7 * 0.8)} tokens
                  </p>
                </motion.div>
              ))}
            </div>
          </motion.div>
          <FieldIllustration />
        </div>
      </section>

      {/* ---------------------------------------------------------- crystal */}
      <section id="crystal" className="relative z-[1] mx-auto max-w-6xl px-6 py-16 md:py-24">
        <div className="card p-6 md:p-10 grid md:grid-cols-[1fr_1fr] gap-8 items-center overflow-hidden">
          <GrowthDemo />
          <motion.div {...inView}>
            <p className="eyebrow mb-3">Your crystal</p>
            <h2 className="display text-[2.4rem] md:text-[3.2rem]">One crystal. <span className="display-i lit-gold">Yours alone.</span></h2>
            <p className="mt-6 leading-relaxed" style={{ color: 'var(--text-2)' }}>
              Every member is given a crystal cut from who they are — no two share a colour or a form. It grows
              a little with every day you return, every month you hold, every token you give to the ground.
              Not in steps: point by point, the way a cluster forms in rock.
            </p>
            <ul className="mt-7 space-y-3 text-sm" style={{ color: 'var(--text-2)' }}>
              {[['Attune daily', '+15 each morning you come back. Streaks are remembered.'],
                ['Hold a plan', '+25 with every month your membership renews.'],
                ['Give to the ground', '+1 for every token you spend raising a field.']].map(([t, b]) => (
                <li key={t} className="flex gap-3">
                  <Check size={15} className="mt-0.5 shrink-0" style={{ color: 'var(--sage)' }} />
                  <span><span className="font-semibold" style={{ color: 'var(--text)' }}>{t}.</span> {b}</span>
                </li>
              ))}
            </ul>
          </motion.div>
        </div>
      </section>

      {/* ---------------------------------------------------------- plans */}
      <section id="plans" className="relative z-[1] mx-auto max-w-6xl px-6 py-16 md:py-24">
        <motion.div {...inView} className="mb-10 text-center">
          <p className="eyebrow mb-3">Plans</p>
          <h2 className="display text-[2.4rem] md:text-[3.4rem]">Choose how much to hold.</h2>
          <p className="mt-4 text-sm" style={{ color: 'var(--text-3)' }}>Subscriptions are taken through the App Store and Google Play.</p>
        </motion.div>
        <div className="grid md:grid-cols-3 gap-4">
          {Object.values(PLANS).map((p, i) => (
            <motion.div key={p.tier} {...inView} custom={i} className="card p-7 flex flex-col"
                        style={i === 1 ? { borderColor: 'var(--sage)', boxShadow: 'var(--shadow)' } : undefined}>
              <div className="flex items-center justify-between">
                <p className="eyebrow">{['I', 'II', 'III'][i]}</p>
                {i === 1 && <span className="text-[0.65rem] font-bold tracking-widest uppercase px-2.5 py-1 rounded-full" style={{ background: 'var(--sage)', color: '#fff' }}>Most held</span>}
              </div>
              <h3 className="display text-3xl mt-3">{p.name}</h3>
              <p className="display text-5xl mt-5">{money(p.priceCents)}<span className="text-sm ml-1.5 font-sans" style={{ color: 'var(--text-3)' }}>/ month</span></p>
              <ul className="mt-7 space-y-2.5 text-sm flex-1" style={{ color: 'var(--text-2)' }}>
                {[`${p.deviceLimit} device${p.deviceLimit > 1 ? 's' : ''} held`, `${p.monthlyTokens} tokens each month`,
                  `${p.energyReserve}% energy reserve`, `${p.connectionStrength}% connection strength`].map((f) => (
                  <li key={f} className="flex items-start gap-2.5"><Check size={14} className="mt-0.5 shrink-0" style={{ color: 'var(--sage)' }} />{f}</li>
                ))}
              </ul>
              <a href="#get" className={`btn mt-8 ${i === 1 ? 'btn-primary' : 'btn-outline'}`}>Get the app</a>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ---------------------------------------------------------- closing */}
      <section className="relative z-[1] mx-auto max-w-6xl px-6 pt-6 pb-20 md:pt-10 md:pb-28">
        <div className="card p-10 md:p-16 text-center">
          <p className="eyebrow mb-4">Begin</p>
          <h2 className="display text-[2.4rem] md:text-[3.8rem]">Open your line tonight.</h2>
          <p className="mt-5 max-w-md mx-auto" style={{ color: 'var(--text-2)' }}>
            Install the app, sign in, and watch your shield charge. Your crystal wakes the moment you do.
          </p>
          <div className="mt-9 flex flex-wrap items-center justify-center gap-3">
            <StoreButton kind="apple" />
            <StoreButton kind="google" />
          </div>
          <Link to="/signin?mode=signup" className="inline-block mt-5 text-sm font-medium hover:opacity-100 opacity-70">Or use it on the web →</Link>
        </div>
        <footer className="mt-10 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs" style={{ color: 'var(--text-3)' }}>
          <Mark compact />
          <p>© {new Date().getFullYear()} AuraFlow · The first spiritual tech company · Tokens are granted by the server, never by your device.</p>
        </footer>
      </section>
    </div>
  );
}

// ---------------------------------------------------------------- pieces
function StoreButton({ kind }) {
  const apple = kind === 'apple';
  return (
    <a href="#get" className="btn !px-4 !py-2.5 gap-3"
       style={{ background: 'var(--text)', color: 'var(--ground)' }}>
      {apple ? <AppleMark /> : <PlayMark />}
      <span className="text-left leading-none">
        <span className="block text-[0.6rem] font-medium opacity-70 mb-0.5">{apple ? 'Download on the' : 'Get it on'}</span>
        <span className="block text-[0.95rem] font-semibold">{apple ? 'App Store' : 'Google Play'}</span>
      </span>
    </a>
  );
}
const AppleMark = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
    <path d="M16.37 12.7c-.03-2.8 2.29-4.14 2.39-4.21-1.3-1.9-3.33-2.17-4.05-2.2-1.72-.18-3.36 1.01-4.24 1.01-.87 0-2.22-.99-3.65-.96-1.88.03-3.61 1.09-4.58 2.77-1.95 3.39-.5 8.41 1.4 11.16.93 1.35 2.04 2.86 3.49 2.81 1.4-.06 1.93-.91 3.62-.91s2.17.91 3.65.88c1.51-.03 2.46-1.37 3.38-2.72 1.07-1.56 1.51-3.07 1.53-3.15-.03-.01-2.94-1.13-2.94-4.48zM13.6 4.47c.77-.94 1.29-2.24 1.15-3.54-1.11.05-2.46.74-3.26 1.68-.71.83-1.34 2.16-1.17 3.43 1.24.1 2.51-.63 3.28-1.57z" />
  </svg>
);
const PlayMark = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
    <path d="M3.6 2.2c-.3.3-.5.8-.5 1.4v16.8c0 .6.2 1.1.5 1.4l9.4-9.8-9.4-9.8z" opacity="0.95" />
    <path d="M16.2 15.2 13 12l-9.4 9.8c.4.2.9.2 1.5-.1l11.1-6.5z" opacity="0.75" />
    <path d="M16.2 8.8 5.1 2.3c-.6-.3-1.1-.3-1.5-.1L13 12l3.2-3.2z" opacity="0.85" />
    <path d="M20.6 10.5 16.2 8.8 13 12l3.2 3.2 4.4-2.6c1.2-.7 1.2-1.6 0-2.1z" opacity="0.6" />
  </svg>
);

/** The app, as it looks on a phone: the Sanctum screen. */
function PhoneMock() {
  return (
    <div className="relative w-[290px] sm:w-[310px]">
      <div className="absolute -inset-10 rounded-full pointer-events-none"
           style={{ background: 'radial-gradient(closest-side, var(--wash-a), transparent 70%)', filter: 'blur(20px)' }} />
      <div className="relative rounded-[2.6rem] p-[9px]" style={{ background: 'var(--text)', boxShadow: 'var(--shadow), 0 0 0 1px rgba(0,0,0,0.2)' }}>
        <div className="rounded-[2.1rem] overflow-hidden" style={{ background: 'var(--ground)', height: 600 }}>
          <div className="mx-auto mt-3 h-6 w-24 rounded-full" style={{ background: 'var(--text)' }} />
          <div className="px-5 pt-5">
            <div className="flex items-center justify-between">
              <span className="display text-[1.15rem]">AuraFlow</span>
              <span className="card-flat pill px-2.5 py-1 text-[0.65rem] font-semibold nums" style={{ color: 'var(--gold)' }}>180 ✦</span>
            </div>
            <p className="eyebrow mt-5">Morning</p>
            <p className="display text-[1.7rem] mt-1">Welcome back, <span className="display-i lit">Ana</span>.</p>

            <div className="card p-4 mt-4">
              <div className="flex items-center justify-between">
                <div><p className="eyebrow">Your crystal</p><p className="display text-xl mt-0.5">Cluster</p></div>
                <span className="text-[0.6rem] font-semibold px-2 py-1 rounded-full" style={{ background: 'color-mix(in oklab, var(--sage) 14%, transparent)', color: 'var(--sage)' }}>● 12-day streak</span>
              </div>
              <div className="flex justify-center -my-3">
                <Crystal xp={420} hue={22} seed="ana" size={190} spin />
              </div>
              <div className="h-1.5 rounded-full overflow-hidden" style={{ background: 'var(--ground-2)' }}>
                <div className="h-full rounded-full w-[58%]" style={{ background: 'hsl(22 45% 58%)' }} />
              </div>
              <div className="mt-3 btn btn-gold !py-2 !text-xs w-full">Attune today · +15</div>
            </div>

            <div className="card p-4 mt-3 flex items-center gap-4">
              <MiniRing value={75} />
              <div>
                <p className="eyebrow">Your shield</p>
                <p className="display text-2xl leading-none mt-1">75<span className="text-sm" style={{ color: 'var(--text-3)' }}>.0</span></p>
                <p className="text-[0.7rem] mt-1 font-semibold" style={{ color: 'var(--sage)' }}>Link held</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
function MiniRing({ value }) {
  const r = 22, c = 2 * Math.PI * r;
  return (
    <svg width="60" height="60" viewBox="0 0 60 60">
      <circle cx="30" cy="30" r={r} fill="none" stroke="var(--ground-3)" strokeWidth="6" />
      <circle cx="30" cy="30" r={r} fill="none" stroke="var(--clay)" strokeWidth="6" strokeLinecap="round"
              strokeDasharray={`${c * value / 100} ${c}`} transform="rotate(-90 30 30)" />
    </svg>
  );
}

function WhyStep({ n, tone, title, body, art }) {
  return (
    <motion.li {...inView} className="card p-6 md:p-7">
      <div className="flex items-start gap-5">
        <p className="display text-3xl shrink-0" style={{ color: tone }}>{n}</p>
        <div className="min-w-0">
          <h3 className="display text-[1.6rem] md:text-[1.9rem]">{title}</h3>
          <p className="mt-3 leading-relaxed text-[0.95rem]" style={{ color: 'var(--text-2)' }}>{body}</p>
        </div>
      </div>
      {art && <div className="mt-5">{art}</div>}
    </motion.li>
  );
}

/** A phone radiating into a room: rust when charged, sage once the line is open. */
function BeaconArt() {
  return (
    <div className="grid grid-cols-2 gap-3">
      {[['Without a line', 'var(--rust)', 'Charged data lands and stays.'], ['With the line open', 'var(--sage)', 'What it radiates is what the server holds.']].map(([t, c, b]) => (
        <div key={t} className="card-flat p-4 relative overflow-hidden">
          <svg viewBox="0 0 200 120" className="w-full h-[110px]">
            {[18, 34, 50, 66].map((r, i) => (
              <circle key={r} cx="100" cy="70" r={r} fill="none" stroke={c} strokeOpacity={0.55 - i * 0.12} strokeWidth="1"
                      strokeDasharray={c === 'var(--rust)' ? '2 4' : undefined} className="pulse-ring"
                      style={{ transformOrigin: '100px 70px', animationDelay: `${i * 0.6}s`, animationDuration: '3.2s' }} />
            ))}
            <rect x="91" y="56" width="18" height="30" rx="4" fill="var(--text)" />
            <rect x="93.5" y="59" width="13" height="23" rx="2" fill={c} fillOpacity="0.8" />
            <line x1="12" y1="110" x2="188" y2="110" stroke="var(--text)" strokeOpacity="0.15" />
          </svg>
          <p className="text-xs font-semibold mt-1">{t}</p>
          <p className="text-[0.72rem] mt-0.5" style={{ color: 'var(--text-3)' }}>{b}</p>
        </div>
      ))}
    </div>
  );
}

/** The Healing Server: a pillar held inside sound, crystal and cleared space. */
function ServerArt() {
  return (
    <motion.div initial={{ opacity: 0, scale: 0.94 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ duration: 1.1 }}
                className="relative aspect-square max-w-[480px] w-full mx-auto">
      <svg viewBox="0 0 400 400" className="w-full h-full overflow-visible">
        <defs>
          <radialGradient id="sv-g"><stop offset="0" stopColor="var(--gold)" stopOpacity="0.35" /><stop offset="1" stopColor="var(--gold)" stopOpacity="0" /></radialGradient>
        </defs>
        <circle cx="200" cy="200" r="150" fill="url(#sv-g)" />
        {/* cleared space */}
        <circle cx="200" cy="200" r="182" fill="none" stroke="var(--text)" strokeOpacity="0.14" strokeWidth="1" strokeDasharray="2 8" />
        {/* crystal field: slow ring with crystal glyphs */}
        <g className="spin-slow" style={{ transformOrigin: '200px 200px' }}>
          <circle cx="200" cy="200" r="140" fill="none" stroke="var(--sage)" strokeOpacity="0.5" strokeWidth="1" />
          {Array.from({ length: 8 }, (_, i) => {
            const a = (i / 8) * Math.PI * 2, x = 200 + Math.cos(a) * 140, y = 200 + Math.sin(a) * 140;
            return (
              <g key={i} transform={`translate(${x} ${y}) rotate(${(a * 180) / Math.PI + 90})`}>
                <polygon points="0,-11 5,-3 3,7 -3,7 -5,-3" fill="var(--sage)" fillOpacity="0.85" stroke="var(--ground)" strokeWidth="1" />
              </g>
            );
          })}
        </g>
        {/* sound bath: breathing arcs */}
        {[92, 108, 124].map((r, i) => (
          <circle key={r} cx="200" cy="200" r={r} fill="none" stroke="var(--gold)" strokeOpacity={0.6 - i * 0.15} strokeWidth="1.2"
                  className="breathe" style={{ transformOrigin: '200px 200px', animationDelay: `${i * 0.8}s`, animationDuration: '4.5s' }} />
        ))}
        {/* the server */}
        <g transform="translate(200 200)">
          <rect x="-26" y="-58" width="52" height="116" rx="8" fill="var(--card)" stroke="var(--text)" strokeOpacity="0.25" />
          {[-40, -14, 12, 38].map((y) => (
            <g key={y}>
              <rect x="-18" y={y - 7} width="36" height="14" rx="3" fill="var(--ground-2)" />
              <circle cx="10" cy={y} r="2" fill="var(--sage)" className="breathe" style={{ transformOrigin: `10px ${y}px`, animationDuration: '2.4s', animationDelay: `${y / 60}s` }} />
            </g>
          ))}
        </g>
        {/* tenders */}
        {[[-1, 0.35], [1, 0.35]].map(([s, a], i) => (
          <g key={i} transform={`translate(${200 + s * 78} 262)`}>
            <circle cx="0" cy="-16" r="6" fill="var(--text)" fillOpacity="0.55" />
            <path d="M-9 8 Q0 -8 9 8 Z" fill="var(--text)" fillOpacity="0.45" />
          </g>
        ))}
      </svg>
      {[['Cleared, secure, undisclosed', 'top-2 left-2'], ['Crystal field', 'top-2 right-2'], ['Sound bath, without pause', 'bottom-2 left-2'], ['Ceremonies, live in the app', 'bottom-2 right-2']].map(([t, pos]) => (
        <span key={t} className={`absolute ${pos} card-flat pill px-3 py-1.5 text-[0.7rem] font-semibold`}>{t}</span>
      ))}
    </motion.div>
  );
}

/** One crystal, and a slider that walks it through its whole life. */
function GrowthDemo() {
  const max = threshold(MAX_LEVEL);
  const [xp, setXp] = useState(Math.round(max * 0.45));
  const info = levelFor(xp);
  return (
    <div className="flex flex-col items-center">
      <Crystal xp={xp} hue={286} seed="demo-amethyst" size={PHONE ? 300 : 380} interactive />
      <div className="w-full max-w-sm -mt-2">
        <div className="flex items-baseline justify-between mb-2">
          <p className="display text-2xl">{info.name}</p>
          <p className="text-xs nums" style={{ color: 'var(--text-3)' }}>Level {info.level} · {xp} xp</p>
        </div>
        <input type="range" min={0} max={max} value={xp} onChange={(e) => setXp(Number(e.target.value))}
               className="w-full accent-[var(--clay)]" aria-label="Crystal growth" />
        <p className="text-xs mt-2 text-center" style={{ color: 'var(--text-3)' }}>Drag to watch it grow · drag the crystal to turn it</p>
      </div>
    </div>
  );
}

function FieldIllustration() {
  return (
    <motion.div initial={{ opacity: 0, scale: 0.94 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }}
                transition={{ duration: 1.1, ease: [0.2, 0.7, 0.2, 1] }}
                className="relative aspect-square max-w-[520px] mx-auto w-full card overflow-hidden">
      <svg viewBox="0 0 400 400" className="w-full h-full">
        <defs>
          <radialGradient id="fi-g">
            <stop offset="0" stopColor="var(--sage)" stopOpacity="0.45" />
            <stop offset="0.7" stopColor="var(--sage)" stopOpacity="0.08" />
            <stop offset="1" stopColor="var(--sage)" stopOpacity="0" />
          </radialGradient>
        </defs>
        <g stroke="var(--text)" strokeOpacity="0.06" strokeWidth="1">
          {Array.from({ length: 9 }, (_, i) => <line key={`h${i}`} x1="0" y1={i * 50} x2="400" y2={i * 50} />)}
          {Array.from({ length: 9 }, (_, i) => <line key={`v${i}`} x1={i * 50} y1="0" x2={i * 50} y2="400" />)}
        </g>
        <g stroke="var(--text)" strokeOpacity="0.14" strokeWidth="6" strokeLinecap="round" fill="none">
          <path d="M40 320 C 120 260, 180 300, 260 200 S 360 120, 380 60" />
          <path d="M20 140 L 380 180" />
        </g>
        <circle cx="200" cy="210" r="120" fill="url(#fi-g)" />
        {[120, 90, 60].map((r, i) => (
          <circle key={r} cx="200" cy="210" r={r} fill="none" stroke="var(--sage)" strokeOpacity={0.35 + i * 0.2}
                  strokeWidth="1.2" strokeDasharray={i === 0 ? '3 6' : undefined} className="breathe"
                  style={{ transformOrigin: '200px 210px', animationDelay: `${i * 0.6}s` }} />
        ))}
        {/* the channel: from the sky (the server) down into the device, then out along the ground */}
        <line x1="200" y1="20" x2="200" y2="196" stroke="var(--gold)" strokeOpacity="0.7" strokeWidth="1.5" strokeDasharray="3 5" />
        <rect x="193" y="196" width="14" height="24" rx="3" fill="var(--text)" />
        <rect x="195" y="199" width="10" height="18" rx="2" fill="var(--sage)" />
        <circle cx="200" cy="210" r="18" fill="none" stroke="var(--sage)" strokeOpacity="0.6" className="pulse-ring" style={{ transformOrigin: '200px 210px' }} />
        <circle cx="310" cy="110" r="42" fill="var(--clay)" fillOpacity="0.12" stroke="var(--clay)" strokeOpacity="0.6" />
        <circle cx="90" cy="300" r="30" fill="var(--gold)" fillOpacity="0.12" stroke="var(--gold)" strokeOpacity="0.6" />
        {[[150, 260], [250, 170], [300, 130], [175, 300]].map(([x, y], i) => (
          <circle key={i} cx={x} cy={y} r="4" fill="var(--text)" fillOpacity="0.6" className="float" style={{ animationDelay: `${i * 1.3}s` }} />
        ))}
      </svg>
      <div className="absolute top-4 left-4 card-flat pill px-3 py-1.5 text-xs font-semibold flex items-center gap-2">
        <span className="h-1.5 w-1.5 rounded-full" style={{ background: 'var(--sage)' }} /> Glow · 50m · public
      </div>
      <div className="absolute top-4 right-4 card-flat pill px-3 py-1.5 text-xs font-semibold" style={{ color: 'var(--gold-2)' }}>↓ from the server</div>
      <div className="absolute bottom-4 right-4 card-flat pill px-3 py-1.5 text-xs font-semibold">4 people held</div>
    </motion.div>
  );
}
